// components/wizard/Abstract.jsx
import React, { useState, useRef } from 'react';
import { Loader } from 'lucide-react';
import RichTextEditor from '../../common/RichTextEditor';
import uploadEditorImage from '../../../utils/uploadEditorImage';

const MIN_ABSTRACT_WORDS = 50;
const MAX_ABSTRACT_WORDS = 300;

const Abstract = ({ formData, updateFormData, onNext, onBack, saving }) => {
  const [wordCount, setWordCount] = useState(0);
  const [exceedsLimit, setExceedsLimit] = useState(false);
  const editorRef = useRef(null);

  // Derived validation. Blank / too short / too long all block the step.
  const wordCountError =
    wordCount === 0
      ? 'Abstract is required'
      : wordCount < MIN_ABSTRACT_WORDS
      ? `Abstract is too short (minimum ${MIN_ABSTRACT_WORDS} words)`
      : wordCount > MAX_ABSTRACT_WORDS
      ? `Abstract must not exceed ${MAX_ABSTRACT_WORDS} words`
      : '';

  const isAbstractValid = wordCountError === '';

  // The editor reports the number now, so this is a real count.
  const handleWordCountChange = (count) => {
    setWordCount(count);
  };

  const handleSubmit = () => {
    if (saving || !isAbstractValid) return;
    onNext();
  };

  return (
    <div>
      <h3 className="text-xl font-semibold mb-6">Abstract</h3>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Abstract <span className="text-red-500">*</span>
        </label>

        <RichTextEditor
          ref={editorRef}
          value={formData.abstract || ''}
          onChange={(content) => updateFormData({ abstract: content })}
          onWordCountChange={handleWordCountChange}
          onExceedsLimit={setExceedsLimit}           // ✅ lowercase 'o'
          onImageExtracted={uploadEditorImage}
          placeholder="Enter your abstract here..."
          minHeight="300px"
          maxHeight="500px"
          showWordCount={true}
          wordLimit={MAX_ABSTRACT_WORDS}
          toolbarOptions="full"
        />

        {wordCount > 0 && !isAbstractValid && (
          <p className="mt-2 text-sm text-red-500">{wordCountError}</p>
        )}
      </div>

      <p className="text-sm text-red-500 mb-6">* Indicates Mandatory Fields</p>

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-8 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Back
        </button>

        <button
          onClick={handleSubmit}
          // isAbstractValid already accounts for the max, so exceedsLimit is optional here.
          // Keep it if you want a belt-and-braces check.
          disabled={saving || !isAbstractValid || exceedsLimit}
          className="px-8 py-3 bg-[--asfi-blue] text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <Loader className="w-4 h-4 animate-spin inline mr-2" />
              Saving...
            </>
          ) : (
            'Save & Continue'
          )}
        </button>
      </div>
    </div>
  );
};

export default Abstract;